/**
 * JobsActions Component
 *
 * Bulk action toolbar for Jobs table showing:
 * - Rerun Jobs (R key)
 * - Cancel Jobs (C key)
 *
 * Follows the pattern used by AssetActions, VulnerabilityActions, SeedsActions
 * for platform consistency.
 */

import {
  ArrowPathIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { useNavigate, useParams } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import {
  type ActionConfigUnion,
  TableActionsBar,
} from '@/components/table/actions';
import { Button } from '@/components/ui/button';
import { useEntitlements } from '@/hooks/useEntitlements';
import { AttacksTab, attacksTabs } from '@/sections/attacks/constants/tabs';
import { useJobsActionHandlers } from '@/sections/jobs/hooks';
import {
  canCancelJob,
  canRerunJob,
} from '@/sections/jobs/utils/jobStatus.util';
import { useAuth } from '@/state/auth';
import { Job } from '@/types';

import { JobsScanStatus } from './JobsScanStatus';

export interface JobsActionsProps {
  /** Jobs to perform actions on */
  jobs: Job[];

  /** Number of selected rows */
  selectedRowsCount: number;

  /** Callback to clear selection after successful action */
  onClearSelection: () => void;

  /** Refetch jobs data after mutations */
  refetch: () => void;

  /** Disable all actions */
  disabled?: boolean;

  /** Hide the "Actions:" label */
  hideLabel?: boolean;

  /** Hide action buttons */
  hideActions?: boolean;

  /** Additional CSS classes */
  className?: string;

  /** Next run text for scan status tooltip */
  nextRunText?: string;

  /** Hide scan status control (for modal contexts where account-wide control is not relevant) */
  hideScanStatus?: boolean;

  /** Hide the "Create Job" button (for modal/embedded contexts where navigating away is not relevant) */
  hideCreateJob?: boolean;

  /** Optional email for checking frozen status of specific customer (for impersonation) */
  email?: string;
}

/**
 * Jobs bulk action component
 *
 * Provides Rerun and Cancel bulk operations for selected jobs.
 * Integrates with TableActionsBar for consistent platform styling.
 *
 * @param props - JobsActions props
 * @returns Action bar component with job-specific operations
 */
export function JobsActions({
  jobs,
  selectedRowsCount: _selectedRowsCount,
  onClearSelection,
  refetch,
  disabled = false,
  hideLabel,
  hideActions,
  className,
  nextRunText = '',
  hideScanStatus = false,
  hideCreateJob = false,
  email,
}: JobsActionsProps) {
  const { canWriteJobs } = useEntitlements();
  const { isPraetorianUser } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();

  // Get bulk action handlers from hook
  const handlers = useJobsActionHandlers({
    selectedJobs: jobs,
    refetch,
  });

  // Filter jobs by eligibility for each action
  // Only jobs in terminal states (Pass/Fail) can be rerun
  // Only jobs in active states (Running/Queued) can be canceled
  const eligibleForRerun = jobs.filter(canRerunJob);
  const eligibleForCancel = jobs.filter(canCancelJob);

  // "Create Job" deep-links to the Attacks → Capabilities tab, which is
  // Praetorian-gated. The `requiresPraetorian` flag on the Capabilities tab (in
  // attacksTabs) is the single source of truth for that gate — read it here so
  // the button and the tab lock stay in sync: flipping the flag updates both
  // surfaces at once. A non-Praetorian user should never be shown a button that
  // only lands them on a feature-locked screen.
  const capabilitiesRequiresPraetorian =
    attacksTabs.find(tab => tab.value === AttacksTab.CAPABILITIES)
      ?.requiresPraetorian ?? false;
  const canCreateJob =
    canWriteJobs && (!capabilitiesRequiresPraetorian || isPraetorianUser);

  // Build actions following Asset table pattern
  // Global scan control first (if shown), then selection-dependent actions
  const actions: ActionConfigUnion[] = [
    // Global scan pause/resume control - rendered first as it's account-wide
    // Hidden in modal contexts or when user lacks write_jobs entitlement
    ...(hideScanStatus || !canWriteJobs
      ? []
      : [
          {
            type: 'Custom' as const,
            position: 'first' as const,
            testId: 'jobs-scan-status',
            render: () => (
              <JobsScanStatus
                refetch={refetch}
                nextRunText={nextRunText}
                email={email}
              />
            ),
          },
        ]),
    // Selection-dependent actions (Rerun/Cancel) - gated by write_jobs entitlement
    ...(canWriteJobs
      ? [
          {
            type: 'Change Status' as const,
            render: () => (
              <div className="flex items-center gap-3">
                <Button
                  data-testid="jobs-rerun-button"
                  data-tutorial="jobs-rerun"
                  onClick={async () => {
                    await handlers.handleRerun(eligibleForRerun);
                    onClearSelection();
                  }}
                  disabled={disabled || eligibleForRerun.length === 0}
                  variant="outline"
                  size="sm"
                  aria-label="Rerun jobs"
                  className="rounded-full"
                >
                  <ArrowPathIcon className="size-4" />
                  Rerun
                </Button>
                <Button
                  data-testid="jobs-cancel-button"
                  data-tutorial="jobs-cancel"
                  onClick={async () => {
                    await handlers.handleCancel(eligibleForCancel);
                    onClearSelection();
                  }}
                  disabled={disabled || eligibleForCancel.length === 0}
                  variant="outline"
                  size="sm"
                  aria-label="Cancel jobs"
                  className="rounded-full"
                >
                  <XMarkIcon className="size-4" />
                  Cancel
                </Button>
              </div>
            ),
          },
        ]
      : []),
    // "Create Job" navigates to Attacks → Capabilities, where jobs are launched.
    // Rendered last (rightmost); gated on write_jobs AND the same Praetorian gate
    // as the Capabilities tab (see canCreateJob above), and hidden in
    // modal/embedded contexts where navigating away is not relevant. Uses the
    // same solid-red styling as the "Pause Scans" control (JobsScanStatus) for
    // consistency.
    ...(hideCreateJob || !canCreateJob
      ? []
      : [
          {
            type: 'Custom' as const,
            position: 'default' as const,
            testId: 'jobs-create-job',
            render: () => (
              <Button
                data-testid="jobs-create-job-button"
                data-tutorial="jobs-create"
                onClick={() =>
                  navigate(
                    `/${userId}/${AuthenticatedRoute.ATTACKS}?tab=capabilities`
                  )
                }
                variant="outline"
                size="sm"
                aria-label="Create job"
                className="bg-chariot-status-critical hover:bg-chariot-status-critical/80 rounded-full border-transparent text-white"
              >
                <PlusIcon className="size-4" />
                Create Job
              </Button>
            ),
          },
        ]),
  ];

  return (
    <TableActionsBar
      selectedItems={jobs}
      actions={actions}
      disabled={disabled}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="jobs"
    />
  );
}
